import { useEffect, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animFrame: number;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const nodes: { x: number; y: number; vx: number; vy: number; pulse: number }[] = [];
    const NODE_COUNT = 55;

    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        n.pulse += 0.018;
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 180) {
            const alpha = (1 - dist / 180) * (isDark ? 0.22 : 0.12);
            const grad = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            grad.addColorStop(0, `rgba(56,214,214,${alpha})`);
            grad.addColorStop(1, `rgba(79,163,255,${alpha})`);
            ctx.beginPath();
            ctx.strokeStyle = grad;
            ctx.lineWidth = 0.7;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        const pulse = (Math.sin(n.pulse) + 1) / 2;
        const r = 1.5 + pulse * 2;
        const alpha = isDark ? 0.35 + pulse * 0.5 : 0.2 + pulse * 0.3;
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56,214,214,${alpha})`;
        ctx.fill();
      });

      animFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener('resize', resize);
    };
  }, [isDark]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className={`relative min-h-screen flex flex-col items-center justify-center overflow-hidden transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-[#F5F5F7]'}`}
    >
      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: isDark
            ? 'linear-gradient(rgba(56,214,214,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,214,214,0.04) 1px, transparent 1px)'
            : 'linear-gradient(rgba(56,214,214,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(56,214,214,0.08) 1px, transparent 1px)',
          backgroundSize: '70px 70px',
        }}
      />

      {/* Neural canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-70" />

      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(56,214,214,0.07) 0%, transparent 70%)'
            : 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(56,214,214,0.12) 0%, transparent 70%)',
        }}
      />

      {/* Bottom fade */}
      <div className={`absolute bottom-0 left-0 w-full h-40 pointer-events-none ${isDark ? 'bg-gradient-to-t from-black to-transparent' : 'bg-gradient-to-t from-[#F5F5F7] to-transparent'}`} />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 text-center pt-28 pb-16">
        {/* Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-10 ${isDark ? 'border-[#38D6D6]/25 bg-[#38D6D6]/5' : 'border-[#38D6D6]/30 bg-[#38D6D6]/8'}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#38D6D6] animate-pulse flex-shrink-0"></span>
          <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">AI Revenue Infrastructure</span>
        </div>

        {/* Headline */}
        <h1 className={`font-sora font-800 leading-[1.04] tracking-tight mb-7 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">Never Miss Another</span>
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
            Job, Case, or Client
          </span>
          <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl">Again</span>
        </h1>

        {/* Subheadline */}
        <p className={`font-grotesk text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-12 ${isDark ? 'text-white/55' : 'text-[#1D1D1F]/55'}`}>
          AI-powered systems that capture, qualify, and convert every inbound opportunity automatically—so your business never loses revenue to missed calls or slow follow-ups.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <button
            onClick={() => scrollTo('contact')}
            className="px-9 py-4 rounded-full font-grotesk font-600 text-base bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF] text-black hover:opacity-90 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Book a Demo
          </button>
          <button
            onClick={() => scrollTo('solution')}
            className={`px-9 py-4 rounded-full font-grotesk font-600 text-base border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              isDark
                ? 'border-white/20 text-white hover:bg-white/8'
                : 'border-black/20 text-[#1D1D1F] hover:bg-black/5'
            }`}
          >
            See How It Works
          </button>
        </div>

        {/* Stats bar */}
        <div className={`inline-flex items-center gap-0 rounded-2xl border overflow-hidden ${isDark ? 'border-white/8 bg-white/[0.03]' : 'border-black/8 bg-black/[0.03]'}`}>
          {[
            { value: '85%', label: 'Fewer Missed Calls' },
            { value: '24/7', label: 'Always-On Coverage' },
            { value: '40%', label: 'More Booked Jobs' },
            { value: '2s', label: 'Avg Response Time' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`px-8 py-5 text-center ${i < 3 ? (isDark ? 'border-r border-white/8' : 'border-r border-black/8') : ''}`}
            >
              <div className="font-sora font-700 text-2xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
                {stat.value}
              </div>
              <div className={`font-grotesk text-xs mt-1 tracking-wide ${isDark ? 'text-white/35' : 'text-black/40'}`}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero visual - floating UI mockup */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <div className={`relative rounded-2xl border overflow-hidden ${isDark ? 'border-white/8 bg-[#0D1117]' : 'border-black/8 bg-white'}`}>
          {/* Mockup header bar */}
          <div className={`flex items-center gap-2 px-5 py-3.5 border-b ${isDark ? 'border-white/5 bg-[#161B22]' : 'border-black/5 bg-[#F5F5F7]'}`}>
            <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400/70"></div>
            <div className="w-3 h-3 rounded-full bg-green-400/70"></div>
            <div className={`ml-4 flex-1 h-6 rounded-md text-xs font-grotesk flex items-center px-3 ${isDark ? 'bg-[#0D1117] text-white/30' : 'bg-white text-black/30'}`}>
              AI Revenue Dashboard — Live
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#38D6D6] animate-pulse"></span>
              <span className={`text-xs font-grotesk ${isDark ? 'text-white/40' : 'text-black/40'}`}>Live</span>
            </div>
          </div>

          {/* Dashboard content */}
          <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Metric cards */}
            {[
              { label: 'Calls Captured Today', value: '47', change: '+12 vs yesterday', icon: 'ri-phone-line', color: '#38D6D6' },
              { label: 'Leads Qualified', value: '31', change: '66% conversion rate', icon: 'ri-user-follow-line', color: '#4FA3FF' },
              { label: 'Revenue Protected', value: '$18,400', change: 'Est. this week', icon: 'ri-money-dollar-circle-line', color: '#38D6D6' },
            ].map((card) => (
              <div key={card.label} className={`rounded-xl p-5 border ${isDark ? 'bg-[#161B22] border-white/5' : 'bg-[#F5F5F7] border-black/5'}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-grotesk ${isDark ? 'text-white/40' : 'text-black/40'}`}>{card.label}</span>
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${card.color}18` }}>
                    <i className={`${card.icon} text-sm`} style={{ color: card.color }}></i>
                  </div>
                </div>
                <div className={`font-sora font-700 text-2xl mb-1 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{card.value}</div>
                <div className="text-xs font-grotesk text-[#38D6D6]">{card.change}</div>
              </div>
            ))}

            {/* Activity feed */}
            <div className={`md:col-span-2 rounded-xl p-5 border ${isDark ? 'bg-[#161B22] border-white/5' : 'bg-[#F5F5F7] border-black/5'}`}>
              <div className={`text-xs font-grotesk mb-4 ${isDark ? 'text-white/40' : 'text-black/40'}`}>Live Activity Feed</div>
              <div className="flex flex-col gap-3">
                {[
                  { time: '2m ago', event: 'AI answered inbound call — HVAC inquiry', status: 'booked', icon: 'ri-phone-line' },
                  { time: '5m ago', event: 'Lead qualified via SMS — Law firm consultation', status: 'qualified', icon: 'ri-message-3-line' },
                  { time: '11m ago', event: 'Follow-up sequence triggered — Property inquiry', status: 'nurturing', icon: 'ri-loop-right-line' },
                  { time: '18m ago', event: 'Appointment booked — Healthcare wellness check', status: 'booked', icon: 'ri-calendar-check-line' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#38D6D6]/10 flex-shrink-0">
                      <i className={`${item.icon} text-[#38D6D6] text-xs`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-grotesk truncate ${isDark ? 'text-white/70' : 'text-black/70'}`}>{item.event}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-grotesk ${
                        item.status === 'booked' ? 'bg-[#38D6D6]/15 text-[#38D6D6]' :
                        item.status === 'qualified' ? 'bg-[#4FA3FF]/15 text-[#4FA3FF]' :
                        'bg-white/8 text-white/40'
                      }`}>{item.status}</span>
                      <span className={`text-xs font-grotesk ${isDark ? 'text-white/25' : 'text-black/25'}`}>{item.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mini chart */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-[#161B22] border-white/5' : 'bg-[#F5F5F7] border-black/5'}`}>
              <div className={`text-xs font-grotesk mb-4 ${isDark ? 'text-white/40' : 'text-black/40'}`}>Capture Rate — 7 Days</div>
              <div className="flex items-end gap-1.5 h-16">
                {[40, 55, 48, 70, 65, 82, 91].map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm" style={{
                    height: `${h}%`,
                    background: i === 6 ? 'linear-gradient(to top, #38D6D6, #4FA3FF)' : isDark ? 'rgba(56,214,214,0.2)' : 'rgba(56,214,214,0.25)',
                  }}></div>
                ))}
              </div>
              <div className={`mt-3 font-sora font-700 text-xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]`}>91%</div>
              <div className={`text-xs font-grotesk ${isDark ? 'text-white/35' : 'text-black/35'}`}>Capture rate today</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5">
        <div className={`w-5 h-8 rounded-full border flex items-start justify-center pt-1.5 ${isDark ? 'border-white/20' : 'border-black/20'}`}>
          <div className={`w-1 h-2 rounded-full animate-bounce ${isDark ? 'bg-white/40' : 'bg-black/40'}`}></div>
        </div>
      </div>
    </section>
  );
}
